import {
  KeyStagesSlug,
  ThreadSlug,
} from "@twinkltech/curriculum-architect-domain-hooks";

import type { UniqueIdentifier } from "@/pages/EditSubjectPlan/types/types";

import { getUniqueLocalId } from "@/utils/getUniqueLocalId/getUniqueLocalId";

export type Unit = {
  id: UniqueIdentifier;
  name: string;
  imageUrl: string;
  keyStages: string[];
};

export type TableResource = {
  id: string;
  name: string;
  keyStagesSlug?: KeyStagesSlug[];
  threadSlugs?: ThreadSlug[];
};

export type BasicResourceCell = {
  id: string;
  column: string;
  row: string;
  resourceId: string;
  span?: number;
  isCustom?: false;
  adaptationNote?: string;
};

export type CutomResourceCell = {
  id: string;
  column: string;
  row: string;
  span?: number;
  isCustom: true;
  title: string;
  description?: string;
};

export type TableData = {
  version: string;
  isModified: boolean;
  isLoading: boolean;
  columns: Array<{
    id: string;
    slug: string;
    title: string;
    order: number;
  }>;
  rows: Array<{
    id: string;
    slug: string;
    title: string;
    order: number;
    keyStages: string[];
  }>;
  cells: (BasicResourceCell | CutomResourceCell)[];
  resources: TableResource[];
};

type InitialiseTableAction = {
  type: "INITIALISE_TABLE";
  payload: TableData;
};

type ResetLoadingFlagAction = {
  type: "RESET_LOADING_FLAG";
};

type CustomCellPayload = {
  isCustom: true;
  title: string;
  description?: string;
  row: string;
  column: string;
  span: number;
};

type AddCellAction = {
  type: "ADD_CELL";
  payload:
    | {
        resourceId: UniqueIdentifier;
        row: string;
        column: string;
      }
    | CustomCellPayload;
};

type UpdateCustomCellAction = {
  type: "UPDATE_CUSTOM_CELL";
  payload: {
    id: string;
    title: string;
    description?: string;
    row: string;
    column: string;
    span?: number;
  };
};

type RemoveCellAction = {
  type: "REMOVE_CELL";
  payload: {
    cellId: UniqueIdentifier;
  };
};

type MoveCellAction = {
  type: "MOVE_CELL";
  payload: {
    cellId: UniqueIdentifier;
    row: string;
    column: string;
  };
};

type ChangeCellSpanRightAction = {
  type: "CHANGE_CELL_SPAN_FROM_RIGHT";
  payload: {
    cellId: UniqueIdentifier;
    span: number;
  };
};

type ChangeCellSpanLeftAction = {
  type: "CHANGE_CELL_SPAN_FROM_LEFT";
  payload: {
    cellId: UniqueIdentifier;
    span: number;
    column: string;
  };
};

export type UpsertCellAdaptationNoteAction = {
  type: "UPSERT_CELL_ADAPTATION_NOTE";
  payload: {
    cellId: UniqueIdentifier;
    adaptationNote: string;
  };
};

export type BatchUpdateTableStructuresPayload = {
  rows?: Array<{
    id: string;
    title?: string;
    order?: number;
    toDelete?: boolean;
  }>;
  columns?: Array<{
    id: string;
    title?: string;
    toDelete?: boolean;
  }>;
};

type BatchUpdateTableStructuresAction = {
  type: "BATCH_UPDATE_TABLE_STRUCTURES";
  payload: BatchUpdateTableStructuresPayload;
};

export type StateWithHistory = {
  past: TableData[];
  present: TableData;
  future: TableData[];
};

type UndoAction = {
  type: "UNDO";
};

type RedoAction = {
  type: "REDO";
};

export type Action =
  | InitialiseTableAction
  | ResetLoadingFlagAction
  | AddCellAction
  | UpdateCustomCellAction
  | RemoveCellAction
  | MoveCellAction
  | ChangeCellSpanRightAction
  | ChangeCellSpanLeftAction
  | UndoAction
  | RedoAction
  | UpsertCellAdaptationNoteAction
  | BatchUpdateTableStructuresAction;

const unitTableDefaultState: TableData = {
  version: "",
  isModified: false,
  isLoading: true,
  columns: [],
  rows: [],
  cells: [],
  resources: [],
};

export const initialState: StateWithHistory = {
  past: [],
  present: unitTableDefaultState,
  future: [],
};

export const unitTableReducer = (
  state: StateWithHistory = initialState,
  action: Action
): StateWithHistory => {
  const { future, past, present } = state;

  // Helper function to handle state updates with optional history
  const updateStateWithHistory = (newPresent: TableData) => {
    // Skip history only if explicitly set to true
    return {
      past: [...past, present],
      present: newPresent,
      future: [],
    };
  };

  /**
   * Cleans up cells in the table when rows or columns are deleted.
   * @param payload The payload containing the rows or columns to delete.
   * @param type The type of deletion ("row" or "column").
   * @returns The updated state after cleanup.
   */
  const cleanupCellsOnAxesDeletion = (
    payload: BatchUpdateTableStructuresAction["payload"],
    type: "row" | "column"
  ) => {
    switch (type) {
      case "row": {
        if (present.rows.length <= 1 || payload.rows === undefined) {
          return state;
        }

        for (const row of payload.rows) {
          const rowIndexToRemove = present.rows.findIndex(
            (r) => r.id === row.id
          );
          if (rowIndexToRemove === -1) {
            return state;
          }

          // Create a new array of cells without the cells that have the row id
          const updatedCells = present.cells.filter(
            (cell) => cell.row !== row.id
          );

          // Create a new array of rows without the row to remove
          const updatedRows = present.rows.filter((r) => r.id !== row.id);

          return {
            past: [...past, present],
            present: {
              ...present,
              rows: updatedRows,
              cells: updatedCells,
              isModified: true,
            },
            future: [],
          };
        }
        return state;
      }

      case "column": {
        if (present.columns.length <= 1 || payload.columns === undefined) {
          return state;
        }

        for (const column of payload.columns) {
          if (!column.toDelete) {
            continue;
          }

          const columnIndexToRemove = present.columns.findIndex(
            (col) => col.id === column.id
          );

          if (columnIndexToRemove === -1) {
            return state;
          }

          // Create a new array of cells without the cells that have the column id
          const updatedCells = present.cells
            .filter((cell) => {
              // Only filter out cells that both start on this column AND have span=1
              // If it spans multiple columns, we'll relocate it in the map step
              return !(
                cell.column === column.id &&
                (!cell.span || cell.span === 1)
              );
            })
            .map((cell) => {
              // If this cell doesn't start on the column being deleted, check span
              if (cell.column !== column.id) {
                if (!cell.span || cell.span === 1) {
                  return cell;
                }

                // For cells that span over the deleted column, reduce span
                const startingColumnIndex = present.columns.findIndex(
                  (col) => col.id === cell.column
                );
                const endingColumnIndex = startingColumnIndex + cell.span - 1;

                // If the column to remove falls within the span range, reduce the span by 1
                if (
                  columnIndexToRemove > startingColumnIndex &&
                  columnIndexToRemove <= endingColumnIndex
                ) {
                  return { ...cell, span: cell.span - 1 };
                }
                return cell;
              }

              // Handle cells that start on the deleted column but span multiple columns
              // We need to move these to start on the next column and reduce span
              if (cell.span && cell.span > 1) {
                // Find the next column after the one being deleted
                const nextColumnIndex = columnIndexToRemove + 1;
                if (nextColumnIndex < present.columns.length) {
                  return {
                    ...cell,
                    column: present.columns[nextColumnIndex].id,
                    span: cell.span - 1,
                  };
                }
              }

              // This should never happen if the filter is working properly
              return cell;
            });

          // Create a new array of columns without the column to remove
          const updatedColumns = present.columns.filter(
            (col) => col.id !== column.id
          );

          return {
            past: [...past, present],
            present: {
              ...present,
              columns: updatedColumns,
              cells: updatedCells,
              isModified: true,
            },
            future: [],
          };
        }
        return state;
      }
    }
  };

  switch (action.type) {
    case "UNDO": {
      if (past.length === 0) return state;
      const previous = past[past.length - 1];
      const newPast = past.slice(0, past.length - 1);

      return {
        ...state,
        past: newPast,
        present: {
          ...previous,
          isModified: true,
        },
        future: [present, ...future],
      };
    }

    case "REDO": {
      if (future.length === 0) return state;
      const next = future[0];
      const newFuture = future.slice(1);

      return {
        ...state,
        past: [...past, present],
        present: {
          ...next,
          isModified: true,
        },
        future: newFuture,
      };
    }

    case "INITIALISE_TABLE":
      if (!present.isLoading) {
        return state;
      }

      // make sure all cells have atleast 1 span
      action.payload.cells.forEach((cell) => {
        if (!cell.span) {
          cell.span = 1;
        }
      });

      return {
        past: [],
        present: {
          ...action.payload,
          isLoading: false,
        },
        future: [],
      };

    case "RESET_LOADING_FLAG": {
      return {
        ...state,
        present: {
          ...present,
          isLoading: true,
        },
      };
    }

    case "ADD_CELL": {
      function isCustomCellPayload(
        payload: AddCellAction["payload"]
      ): payload is CustomCellPayload {
        return "isCustom" in payload && payload.isCustom === true;
      }

      const cell = isCustomCellPayload(action.payload)
        ? {
            id: getUniqueLocalId(),
            row: action.payload.row,
            column: action.payload.column,
            span: action.payload.span,
            isCustom: true as const,
            title: action.payload.title,
            description: action.payload.description,
          }
        : {
            id: getUniqueLocalId(),
            resourceId: action.payload.resourceId,
            row: action.payload.row,
            column: action.payload.column,
            span: 1,
          };

      const newPresent = {
        ...present,
        isModified: true,
        cells: [...present.cells, cell],
      };

      return updateStateWithHistory(newPresent);
    }

    case "UPDATE_CUSTOM_CELL": {
      const updatedCells = present.cells.map((cell) =>
        action.payload.id === cell.id && "isCustom" in cell && cell.isCustom
          ? {
              ...cell,
              title: action.payload.title,
              description: action.payload.description,
              row: action.payload.row,
              column: action.payload.column,
              span: action.payload.span || 1,
            }
          : cell
      );

      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "REMOVE_CELL": {
      const updatedCells = present.cells.filter(
        (cell) => cell.id !== action.payload.cellId
      );

      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "MOVE_CELL": {
      // Find the index of the cell to move
      const cellIndexToMove = present.cells.findIndex(
        (c) => c.id === action.payload.cellId
      );

      // If the cell is not found, return the current state
      if (cellIndexToMove === -1) {
        return state;
      }

      // Create a new array of cells with the updated cell
      const updatedCells = present.cells.map((cell, index) =>
        index === cellIndexToMove
          ? {
              ...cell,
              row: action.payload.row,
              column: action.payload.column,
              span: 1,
            }
          : cell
      );

      // Return the new state with the updated cells array
      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "CHANGE_CELL_SPAN_FROM_RIGHT": {
      if (action.payload.span < 1) {
        return state;
      }

      // if the span is the same as current, do nothing
      const cellToUpdate = present.cells.find(
        (cell) => cell.id === action.payload.cellId
      );
      if (!cellToUpdate) {
        return state;
      }
      if (cellToUpdate.span === action.payload.span) {
        return state;
      }

      const updatedCells = present.cells.map((cell) =>
        action.payload.cellId === cell.id
          ? {
              ...cell,
              span: action.payload.span,
            }
          : cell
      );

      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "CHANGE_CELL_SPAN_FROM_LEFT": {
      if (action.payload.span < 1) {
        return state;
      }

      // if the span is the same as current, do nothing
      const cellToUpdate = present.cells.find(
        (cell) => cell.id === action.payload.cellId
      );
      if (!cellToUpdate) {
        return state;
      }
      if (cellToUpdate.span === action.payload.span) {
        return state;
      }

      const updatedCells = present.cells.map((cell) =>
        action.payload.cellId === cell.id
          ? {
              ...cell,
              span: action.payload.span,
              column: action.payload.column,
            }
          : cell
      );

      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "UPSERT_CELL_ADAPTATION_NOTE": {
      const updatedCells = present.cells.map((cell) =>
        action.payload.cellId === cell.id && !cell.isCustom
          ? {
              ...cell,
              adaptationNote: action.payload.adaptationNote,
            }
          : cell
      );
      const newPresent = {
        ...present,
        cells: updatedCells,
        isModified: true,
      };

      return updateStateWithHistory(newPresent);
    }

    case "BATCH_UPDATE_TABLE_STRUCTURES": {
      // Start with current state
      const updatedState = { ...present };

      if (
        action.payload.rows === undefined &&
        action.payload.columns === undefined
      ) {
        return state;
      }

      if (action.payload.rows) {
        const rowUpdates = action.payload.rows;

        // Step 1: Clean up cells for rows marked for deletion
        const rowsToDelete = rowUpdates.filter((r) => r.toDelete);
        if (rowsToDelete.length > 0) {
          const stateAfterCleanup = cleanupCellsOnAxesDeletion(
            { rows: rowsToDelete },
            "row"
          );

          if (stateAfterCleanup !== state) {
            updatedState.cells = stateAfterCleanup.present.cells;
            updatedState.rows = stateAfterCleanup.present.rows;
          }
        }

        // Step 2: Handle all row operations in a single pass
        // - Remove rows marked for deletion
        // - Update properties of existing rows
        // - Track IDs of rows we've seen
        const seenRowIds = new Set<string>();
        const processedRows = updatedState.rows
          .filter((row) => {
            // Check if this row should be deleted
            const shouldDelete = rowUpdates.some(
              (update) => update.id === row.id && update.toDelete
            );

            if (shouldDelete) {
              updatedState.isModified = true;
              return false;
            }

            // Mark this row ID as seen
            seenRowIds.add(row.id);
            return true;
          })
          .map((row) => {
            // Check if this row should be updated
            const update = rowUpdates.find(
              (update) =>
                update.id === row.id &&
                (update.title !== undefined || update.order !== undefined)
            );

            if (!update) {
              return row;
            }

            // Apply updates
            updatedState.isModified = true;
            return {
              ...row,
              title: update.title || row.title,
              order: update.order || row.order,
            };
          });

        // Step 3: Add new rows (those in payload but not in current state)
        const newRows = rowUpdates.filter(
          (update) => !seenRowIds.has(update.id) && !update.toDelete
        );

        if (newRows.length > 0) {
          updatedState.rows = [
            ...processedRows,
            ...newRows.map((row) => ({
              id: row.id,
              title: row.title || "New Row",
              slug: row.id.toLowerCase().replace(/\s+/g, "-"),
              order: row.order || processedRows.length + 1,
              keyStages: [],
            })),
          ];
          updatedState.isModified = true;
        } else {
          updatedState.rows = processedRows;
        }
      }

      if (action.payload.columns) {
        const columnUpdates = action.payload.columns;

        // Step 1: Clean up cells for columns marked for deletion
        const columnsToDelete = columnUpdates.filter((c) => c.toDelete);
        if (columnsToDelete.length > 0) {
          const stateAfterColumnCleanup = cleanupCellsOnAxesDeletion(
            { columns: columnsToDelete },
            "column"
          );
          if (stateAfterColumnCleanup !== state) {
            updatedState.cells = stateAfterColumnCleanup.present.cells;
            updatedState.columns = stateAfterColumnCleanup.present.columns;
          }
        }

        // Step 2: Handle all column operations in a single pass
        // - Remove columns marked for deletion
        // - Update properties of existing columns
        // - Track IDs of columns we've seen
        const seenColumnIds = new Set<string>();
        const processedColumns = updatedState.columns
          .filter((column) => {
            // Check if this column should be deleted
            const shouldDelete = columnUpdates.some(
              (update) => update.id === column.id && update.toDelete
            );

            if (shouldDelete) {
              updatedState.isModified = true;
              return false;
            }

            // Mark this column ID as seen
            seenColumnIds.add(column.id);
            return true;
          })
          .map((column) => {
            // Check if this column should be updated
            const update = columnUpdates.find(
              (update) => update.id === column.id && update.title !== undefined
            );

            if (!update) {
              return column;
            }

            // Apply updates
            updatedState.isModified = true;
            return {
              ...column,
              title: update.title || column.title,
            };
          });

        // Step 3: Add new columns (those in payload but not in current state)
        const newColumns = columnUpdates.filter(
          (update) => !seenColumnIds.has(update.id) && !update.toDelete
        );

        if (newColumns.length > 0) {
          updatedState.columns = [
            ...processedColumns,
            ...newColumns.map((column) => ({
              id: column.id,
              title: column.title || "New Column",
              slug: column.id.toLowerCase().replace(/\s+/g, "-"),
              order: processedColumns.length + 1,
            })),
          ];
          updatedState.isModified = true;
        } else {
          updatedState.columns = processedColumns;
        }
      }

      // Mark as modified and return with history
      updatedState.isModified = true;
      return updateStateWithHistory(updatedState);
    }

    default:
      console.warn("No action triggered", action);
      break;
  }

  return state;
};
